import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import {
    PerformanceState,
    PerformanceCue,
    PerformanceCueProjection,
    FiredCue,
    ShowMarker,
    QuickNote,
    CallerLayout,
} from './types';

// ── Slice Interface ──────────────────────────────────────────────

export interface PerformanceSlice {
    performance: PerformanceState;
    performanceActions: {
        /** Populate cue sequence and prepare for a new show */
        initPerformance: (cueSequence: PerformanceCue[]) => void;
        /** Snapshot a ready cue projection and start the show */
        startShowFromProjection: (projection: PerformanceCueProjection) => void;
        /** Start the show clock */
        startShow: () => void;
        /** Fire the current cue and advance */
        fireCue: (uid: string) => void;
        /** Skip current cue without firing */
        skipCue: () => void;
        /** Step back to previous cue */
        previousCue: () => void;
        /** Add a freeform show marker */
        addMarker: (label: string) => void;
        /** Start intermission */
        startIntermission: () => void;
        /** End intermission and resume show */
        endIntermission: () => void;
        /** Add a quick timestamped note */
        addQuickNote: (text: string) => void;
        /** End the show */
        endShow: () => void;
        /** Set the preferred caller layout */
        setCallerLayout: (layout: CallerLayout) => void;
        /** Reset to idle state for next performance */
        resetPerformance: () => void;
        /** Clear all performance data on logout */
        clearPerformance: () => void;
    };
}

// ── Initial State ────────────────────────────────────────────────

const INITIAL_PERFORMANCE_STATE: PerformanceState = {
    status: 'idle',
    performanceId: null,
    projectionFingerprint: null,
    cueSequence: [],
    currentIndex: 0,
    firedCues: [],
    showStartTime: null,
    showEndTime: null,
    intermission: {
        active: false,
        startTime: null,
        durationMinutes: 15,
    },
    markers: [],
    quickNotes: [],
    callerLayout: 'cue-focused',
};

// ── Slice Creator ────────────────────────────────────────────────

export const createPerformanceSlice: StateCreator<
    AppState,
    [['zustand/immer', never]],
    [],
    PerformanceSlice
> = (set) => ({
    performance: { ...INITIAL_PERFORMANCE_STATE },
    performanceActions: {
        initPerformance: (cueSequence) => {
            set((state) => {
                state.performance = {
                    ...INITIAL_PERFORMANCE_STATE,
                    performanceId: crypto.randomUUID(),
                    cueSequence,
                    callerLayout: state.performance.callerLayout, // preserve preference
                };
            });
        },

        startShowFromProjection: (projection) => {
            set((state) => {
                if (state.performance.status !== 'idle') return;
                if (projection.readiness.status !== 'ready') return;
                if (projection.cues.length === 0) return;

                state.performance = {
                    ...INITIAL_PERFORMANCE_STATE,
                    performanceId: crypto.randomUUID(),
                    projectionFingerprint: projection.fingerprint,
                    cueSequence: projection.cues,
                    quickNotes: state.performance.quickNotes,
                    status: 'live',
                    showStartTime: Date.now(),
                    callerLayout: state.performance.callerLayout,
                };
            });
        },

        startShow: () => {
            set((state) => {
                if (state.performance.status !== 'idle') return;
                state.performance.status = 'live';
                state.performance.showStartTime = Date.now();
            });
        },

        fireCue: (uid) => {
            set((state) => {
                const perf = state.performance;
                if (perf.status !== 'live' && perf.status !== 'intermission') return;
                if (perf.currentIndex >= perf.cueSequence.length) return;

                const currentCue = perf.cueSequence[perf.currentIndex]!;
                const fired: FiredCue = {
                    cueId: currentCue.id,
                    firedAt: Date.now(),
                    firedBy: uid,
                };
                perf.firedCues.push(fired);
                perf.currentIndex = Math.min(perf.currentIndex + 1, perf.cueSequence.length);
            });
        },

        skipCue: () => {
            set((state) => {
                const perf = state.performance;
                if (perf.currentIndex >= perf.cueSequence.length) return;
                perf.currentIndex = Math.min(perf.currentIndex + 1, perf.cueSequence.length);
            });
        },

        previousCue: () => {
            set((state) => {
                const perf = state.performance;
                if (perf.currentIndex <= 0) return;
                perf.currentIndex -= 1;
            });
        },

        addMarker: (label) => {
            set((state) => {
                const marker: ShowMarker = {
                    id: crypto.randomUUID(),
                    label,
                    timestamp: Date.now(),
                };
                state.performance.markers.push(marker);
            });
        },

        startIntermission: () => {
            set((state) => {
                const perf = state.performance;
                if (perf.status !== 'live') return;
                perf.status = 'intermission';
                perf.intermission.active = true;
                perf.intermission.startTime = Date.now();
            });
        },

        endIntermission: () => {
            set((state) => {
                const perf = state.performance;
                if (perf.status !== 'intermission') return;
                perf.status = 'live';
                perf.intermission.active = false;
            });
        },

        addQuickNote: (text) => {
            set((state) => {
                const note: QuickNote = {
                    id: crypto.randomUUID(),
                    text,
                    timestamp: Date.now(),
                };
                state.performance.quickNotes.push(note);
            });
        },

        endShow: () => {
            set((state) => {
                const perf = state.performance;
                if (perf.status === 'idle' || perf.status === 'ended') return;
                perf.status = 'ended';
                perf.showEndTime = Date.now();
                // End intermission if still active
                if (perf.intermission.active) {
                    perf.intermission.active = false;
                }
            });
        },

        setCallerLayout: (layout) => {
            set((state) => {
                state.performance.callerLayout = layout;
            });
        },

        resetPerformance: () => {
            set((state) => {
                const preservedLayout = state.performance.callerLayout;
                state.performance = {
                    ...INITIAL_PERFORMANCE_STATE,
                    callerLayout: preservedLayout,
                };
            });
        },

        clearPerformance: () => {
            set((state) => {
                state.performance = { ...INITIAL_PERFORMANCE_STATE };
            });
        },
    },
});
